import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
    DadosLogin,
    PortaisRotas,
} from 'src/app/auth/interfaces/dados-login.interface';
import { AuthService } from 'src/app/auth/services/auth.service';
import { ColaboradorService } from 'src/app/modules/colaboradores/services/colaborador.service';

export interface Portal {
    titulo: string;
    link: string;
}

export type Portais = Portal[];

@Injectable({
    providedIn: 'root',
})
export class PortalService {
    private readonly loadingSubject = new BehaviorSubject<boolean>(true);
    readonly loading$ = this.loadingSubject.asObservable();
    private readonly tituloSubject = new BehaviorSubject<string>('');
    readonly titulo$ = this.tituloSubject.asObservable();
    portais: Portais = [];

    private readonly portalSubject = new BehaviorSubject<string | undefined>(
        undefined
    );
    private set currentRole(role: string | undefined) {
        this.portalSubject.next(role);
        if (role) this.tituloSubject.next(`Portal ${role}`);
        else this.tituloSubject.next('');
    }
    private get currentRole(): string | undefined {
        return this.portalSubject.value;
    }

    readonly portal$ = this.portalSubject
        .asObservable()
        .pipe(map(role => role?.toLowerCase()));

    get portal() {
        return this.portalSubject.value?.toLowerCase();
    }

    constructor(
        private router: Router,
        private authService: AuthService,
        private apiColaboradorService: ColaboradorService
    ) {
        this.observarDadosLogin();
        this.observarRota();
    }

    private observarRota() {
        this.router.events
            .pipe(
                filter(
                    (e: Event): e is NavigationEnd => e instanceof NavigationEnd
                )
            )
            .subscribe((e: NavigationEnd) => {
                this.setRoleFromRoute(e.urlAfterRedirects);
            });
    }

    private observarDadosLogin() {
        this.authService.dadosLogin().subscribe(dados => {
            if (!dados || dados instanceof Error) {
                this.loadingSubject.next(true);
                return;
            }

            this.loadingSubject.next(false);
            this.portais = dados.grupos
                .map(grupo => {
                    try {
                        const rota = this.getRouteFromGrupoUsuario(grupo);
                        return {
                            titulo: grupo.id_grupo_usuario.grupo_usuario,
                            link: `/${rota}`,
                        };
                    } catch (error) {
                        console.error(error);
                        return null;
                    }
                })
                .filter(portal => portal != null);
        });
    }

    private setRoleFromRoute(routeUrl: string) {
        if (routeUrl.startsWith('/publico')) {
            this.setRole('Público');
        } else if (routeUrl.startsWith('/colaborador')) {
            this.setRole('Colaborador');
        } else if (routeUrl.startsWith('/administrador')) {
            this.setRole('Administrador');
        } else if (routeUrl.startsWith('/coordenador')) {
            this.setRole('Coordenador');
        } else if (routeUrl.startsWith('/gestor')) {
            this.setRole('Gestor');
        } else if (
            routeUrl.startsWith('/auth') ||
            routeUrl.startsWith('/pages')
        )
            this.setRole(null);
        else
            throw new Error(`O papel de acesso ${routeUrl} não foi encontrado`);
    }

    getRouteFromGrupoUsuario(grupoUsuario: DadosLogin['grupos'][0]) {
        return this.getRouteFromNomeGrupoUsuario(
            grupoUsuario.id_grupo_usuario.grupo_usuario
        );
    }

    getRouteFromNomeGrupoUsuario(nomeGrupoUsuario: string) {
        if (nomeGrupoUsuario === 'Público') {
            return '/publico';
        }
        if (nomeGrupoUsuario === 'Colaborador') {
            return '/colaborador';
        }
        if (nomeGrupoUsuario === 'Administrador') {
            return '/administrador';
        }
        if (nomeGrupoUsuario === 'Coordenador') {
            return '/coordenador';
        }
        if (nomeGrupoUsuario === 'Gestor') {
            return '/gestor';
        }
        throw new Error(
            `O portal não existe: ${JSON.stringify(nomeGrupoUsuario)}`
        );
    }

    setRole(role: string | undefined) {
        if (this.currentRole != undefined && role === this.currentRole) return;
        this.currentRole = role;
        console.log(`Role set to ${role}`);
        if (role === 'Colaborador') {
            if (this.authService.current_user) {
                this.apiColaboradorService.setColaboradorDados(
                    this.authService.current_user
                );
            } else {
                this.authService.getCurrentColaborador().subscribe(res => {
                    this.apiColaboradorService
                        .colaboradorDados(res.id_colaborador)
                        .subscribe(res => {
                            this.authService.current_user = res;
                            console.log('dados do colaborador carregados');
                        });
                });
            }
        }
    }

    getRole() {
        return this.currentRole as keyof PortaisRotas;
    }

    getRoleRoute() {
        if (this.currentRole)
            return this.getRouteFromNomeGrupoUsuario(this.currentRole);
        else throw new Error('O papel atual não foi definido');
    }
}
